import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, Clock, MapPin, Shield, Globe, Truck, ExternalLink, Award, Users } from 'lucide-react';
import { companyInfo, contactInfo, services } from '../data/mock';
import logo from '../assets/images/logo.png';

// Clean animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const Footer = () => {
  const servicesItems = services.slice(0, 4).map(service => ({
    name: service.title,
    path: '/services'
  }));

  const companyItems = [
    { name: "About Us", path: "/about" },
    { name: "Our Fleet", path: "/fleet" },
    { name: "Coverage Areas", path: "/coverage" },
    { name: "Safety Standards", path: "/safety" }
  ];

  const resourcesItems = [
    { name: "Get Quote", path: "/contact" },
    { name: "Track Shipment", path: "/tracking" },
    { name: "Customer Portal", path: "/portal" },
    { name: "Documentation", path: "/docs" }
  ];

  return (
    <footer className="bg-white border-t border-gray-200">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        
        {/* Company Header */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16 pb-12 border-b border-gray-200"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Company Info */}
            <motion.div variants={fadeIn}>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 rounded-xl overflow-hidden shadow-lg border border-gray-200">
                  <img 
                    src={logo} 
                    alt="Lepus Group Inc" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{companyInfo.name}</h2>
                  <p className="text-[#1d526d] font-semibold tracking-wide">FREIGHT & TRANSPORTATION</p>
                  <p className="text-gray-600 text-sm">Excellence Since 2006</p>
                </div>
              </div>
              
              <p className="text-gray-600 leading-relaxed text-lg max-w-2xl">
                North America's trusted transportation partner, delivering excellence through 
                innovation, reliability, and unmatched customer service for over 18 years.
              </p>
            </motion.div>

            {/* Quick Contact */}
            <motion.div variants={fadeIn} className="lg:text-right">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Ready to Ship?</h3>
              <div className="space-y-4 mb-6">
                <a 
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="flex lg:justify-end items-center space-x-3 text-[#1d526d] hover:text-[#2a6b89] transition-colors group"
                >
                  <Phone className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-bold text-lg">{companyInfo.phone}</span>
                </a>
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="flex lg:justify-end items-center space-x-3 text-gray-600 hover:text-[#1d526d] transition-colors group"
                >
                  <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-semibold">{companyInfo.email}</span>
                </a>
              </div>
              
              <Link
                to="/contact"
                className="bg-[#1d526d] hover:bg-[#2a6b89] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center group"
              >
                Get Free Quote
                <ExternalLink className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </motion.div>
        
        {/* Footer Links Grid */}
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-5 gap-8"
        >
          
          {/* Contact Information */}
          <motion.div variants={fadeIn} className="lg:col-span-2">
            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
              <Phone className="w-5 h-5 text-[#1d526d] mr-2" />
              Contact Information
            </h3>
            
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Main Contact */}
                <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                  <div className="text-[#1d526d] font-semibold text-sm mb-1">Main Office</div>
                  <a href={`tel:${companyInfo.phoneRaw}`} className="text-gray-900 font-bold hover:text-[#1d526d] transition-colors">
                    {companyInfo.phone}
                  </a>
                  <div className="text-gray-600 text-sm mt-1">General Inquiries</div>
                </div>

                {/* Operations */}
                <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                  <div className="text-[#1d526d] font-semibold text-sm mb-1">Operations</div>
                  <a href={`tel:${contactInfo.operations.phoneRaw}`} className="text-gray-900 font-bold hover:text-[#1d526d] transition-colors">
                    {contactInfo.operations.phone}
                  </a>
                  <div className="text-gray-600 text-sm mt-1">24/7 Dispatch</div>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-[#1d526d] mt-0.5" />
                <div>
                  <div className="text-gray-900 font-semibold">{companyInfo.hours}</div>
                  <div className="text-gray-600 text-sm">Emergency dispatch available 24/7</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Services */}
          <motion.div variants={fadeIn}>
            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
              <Truck className="w-5 h-5 text-[#1d526d] mr-2" />
              Services
            </h3>
            
            <div className="space-y-3">
              {servicesItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className="block text-gray-600 hover:text-[#1d526d] transition-colors duration-200 font-medium hover:translate-x-1 transform transition-transform"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Company */}
          <motion.div variants={fadeIn}>
            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
              <Globe className="w-5 h-5 text-[#1d526d] mr-2" />
              Company
            </h3>
            
            <div className="space-y-3">
              {companyItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className="block text-gray-600 hover:text-[#1d526d] transition-colors duration-200 font-medium hover:translate-x-1 transform transition-transform"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Resources */}
          <motion.div variants={fadeIn}>
            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
              <Shield className="w-5 h-5 text-[#1d526d] mr-2" />
              Resources
            </h3>
            
            <div className="space-y-3">
              {resourcesItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className="block text-gray-600 hover:text-[#1d526d] transition-colors duration-200 font-medium hover:translate-x-1 transform transition-transform"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-16 pt-12 border-t border-gray-200"
        >
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <motion.div variants={fadeIn} className="flex flex-col items-center">
              <div className="w-12 h-12 bg-[#1d526d] rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Fully Licensed & Insured</h4>
              <p className="text-gray-600 text-sm">$2M liability coverage with comprehensive cargo protection</p>
            </motion.div>

            <motion.div variants={fadeIn} className="flex flex-col items-center">
              <div className="w-12 h-12 bg-[#1d526d] rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Industry Certified</h4>
              <p className="text-gray-600 text-sm">DOT compliant with safety awards and industry recognition</p>
            </motion.div>

            <motion.div variants={fadeIn} className="flex flex-col items-center">
              <div className="w-12 h-12 bg-[#1d526d] rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">18+ Years Experience</h4>
              <p className="text-gray-600 text-sm">Trusted by thousands of clients across North America</p>
            </motion.div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-16 pt-12 border-t border-gray-200 text-center"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Need Transportation Services?
          </h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Get a free quote today and experience the reliability and professionalism 
            that has made us North America's trusted transportation partner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#1d526d] hover:bg-[#2a6b89] text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center justify-center group"
            >
              Get Free Quote
              <ExternalLink className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={`tel:${companyInfo.phoneRaw}`}
              className="border-2 border-[#1d526d] text-[#1d526d] hover:bg-[#1d526d] hover:text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 inline-flex items-center justify-center"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call {companyInfo.phone}
            </a>
          </div>
        </motion.div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-gray-600 text-center lg:text-left"
            >
              © 2006-2024 <span className="font-bold text-gray-900">{companyInfo.name}</span>. All rights reserved. | 
              <span className="text-[#1d526d] font-semibold"> Licensed Transportation Provider</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-wrap justify-center space-x-6 text-gray-600"
            >
              <Link to="/privacy" className="hover:text-[#1d526d] transition-colors font-medium">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-[#1d526d] transition-colors font-medium">Terms of Service</Link>
              <Link to="/accessibility" className="hover:text-[#1d526d] transition-colors font-medium">Accessibility</Link>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;